import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/Card.tsx';
import { Button } from '../components/ui/Button.tsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Clock, Zap, Shield, CreditCard, Star } from 'lucide-react';
import { createStripeSession, getUserSubscriptionStatus } from '../services/apiService.ts';
import { useSession } from '../hooks/useSession.ts';
import Navbar from '../components/Navbar.tsx';
import Footer from '../components/Footer.tsx';
import { ONE_TIME_SCAN_LIMIT_MB, PRO_PLAN_SCAN_LIMIT_MB } from '../constants/scanLimits.ts';
import { LanguageToggle } from '../components/LanguageToggle.tsx';
import { Logo } from '../components/Logo.tsx';

export default function UpgradeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session, loading: authLoading } = useSession();
  const user = session?.user;
  const [loading, setLoading] = useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = React.useState<any>(null);
  const cancelled = searchParams.get('cancelled') === 'true';

  React.useEffect(() => {
    console.log('[UpgradeScreen] Component mounted');
    if (cancelled) {
      setError('Payment was cancelled. You can try again anytime.');
    }
  }, [cancelled]);

  React.useEffect(() => {
    async function checkSubscriptionStatus() {
      if (user) {
        console.log('[UpgradeScreen] Checking subscription status for user:', user.id);
        try {
          const status = await getUserSubscriptionStatus();
          setSubscriptionStatus(status);
          console.log('[UpgradeScreen] Subscription status:', status);
        } catch (err) {
          console.error('[UpgradeScreen] Failed to check subscription status:', err);
        }
      }
    }

    if (!authLoading) {
      checkSubscriptionStatus();
    }
  }, [user, authLoading]);

  const handlePlanSelection = async (plan: 'starter' | 'pro') => {
    if (!user) {
      console.log('[UpgradeScreen] No user logged in, redirecting to login');
      navigate('/signup?redirect=/upgrade');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log(`[UpgradeScreen] User selected plan: ${plan}`);
      
      const result = await createStripeSession(plan, { token: session?.access_token || '' });
      console.log(`[UpgradeScreen] Stripe session created:`, result);
      
      if (result.session_url) {
        console.log(`[UpgradeScreen] Redirecting to Stripe checkout`);
        window.location.href = result.session_url;
      } else {
        throw new Error('No session URL returned from Stripe');
      }
    } catch (error) {
      console.error(`[UpgradeScreen] Error creating checkout session:`, error);
      setError((error as Error)?.message || 'Failed to create checkout session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Only show 'all set' if user is Pro or has one-time scans with scans_remaining > 0
  const hasActiveOneTimeScans = subscriptionStatus?.hasOneTimeScans && Array.isArray(subscriptionStatus?.entitlements) && subscriptionStatus.entitlements.some((ent: any) => ent.scans_remaining > 0);
  if (subscriptionStatus?.isProUser || hasActiveOneTimeScans) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <Check className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-4xl font-bold mb-4">You're All Set! 🎉</h1>
              
              {subscriptionStatus.isProUser && (
                <div className="bg-blue-900/30 rounded-lg p-6 mb-6">
                  <h2 className="text-2xl font-semibold text-blue-400 mb-2">Pro Subscription Active</h2>
                  <p className="text-gray-300 mb-4">You have unlimited access to all premium features.</p>
                  {subscriptionStatus.subscription && (
                    <div className="text-sm text-gray-400">
                      <p>Scans used this period: {subscriptionStatus.subscription.scans_used} / {subscriptionStatus.subscription.scan_limit || 'Unlimited'}</p>
                      <p>AI fixes used: {subscriptionStatus.subscription.ai_fixes_used || 0} / {subscriptionStatus.subscription.ai_fixes_limit || 25}</p>
                      <p>Next billing: {new Date(subscriptionStatus.subscription.current_period_end).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              )}
              
              {subscriptionStatus.hasOneTimeScans && (
                <div className="bg-yellow-900/30 rounded-lg p-6 mb-6">
                  <h2 className="text-2xl font-semibold text-yellow-400 mb-2">Starter Pack Active</h2>
                  <p className="text-gray-300 mb-4">You have {subscriptionStatus.entitlements?.reduce((sum: number, ent: any) => sum + ent.scans_remaining, 0)} scans remaining.</p>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <button
                onClick={() => navigate('/upload')}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition inline-flex items-center gap-2"
              >
                <Zap className="h-5 w-5" />
                Start Scanning →
              </button>
              
              {!subscriptionStatus.isProUser && (
                <div className="mt-8">
                  <p className="text-gray-400 mb-4">Want unlimited scans and AI fixes? Upgrade to Pro:</p>
                  <button
                    onClick={() => handlePlanSelection('pro')}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Processing...' : 'Upgrade to Pro'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <main className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent">
              Choose Your Plan
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Start with our free tier, upgrade to Starter for more scans, or go Pro for unlimited scanning and AI-powered fix recommendations.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="max-w-2xl mx-auto mb-8 bg-red-900/30 border border-red-700 rounded-lg p-4">
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            
            {/* Free Plan */}
            <div className="bg-gray-900 rounded-2xl p-8 border border-gray-700">
              <div className="text-center mb-8">
                <div className="bg-gray-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Free</h3>
                <div className="text-4xl font-bold mb-2">$0</div>
                <p className="text-gray-400">Get started with basic scanning</p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>3 security scans</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Basic vulnerability detection</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Up to 50MB file uploads</span>
                </li>
                <li className="flex items-center gap-3 text-gray-500">
                  <span className="h-5 w-5">×</span>
                  <span>No AI fix recommendations</span>
                </li>
              </ul>

              <div className="text-center">
                <p className="text-gray-400 text-sm">Already included with signup</p>
              </div>
            </div>

            {/* Starter Plan */}
            <div className="bg-gradient-to-br from-blue-900/30 to-purple-900/30 rounded-2xl p-8 border-2 border-blue-500">
              <div className="text-center mb-8">
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Starter Pack</h3>
                <div className="text-4xl font-bold mb-2">$5<span className="text-lg text-gray-400"> one-time</span></div>
                <p className="text-gray-400">Perfect for small projects</p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>10 security scans</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Full vulnerability detection</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Up to 50MB file uploads</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Privacy & compliance checks</span>
                </li>
                <li className="flex items-center gap-3 text-gray-500">
                  <span className="h-5 w-5">×</span>
                  <span>No AI fix recommendations</span>
                </li>
              </ul>

              <button
                onClick={() => handlePlanSelection('starter')}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    Get Starter Pack
                  </>
                )}
              </button>
            </div>

            {/* Pro Subscription */}
            <div className="bg-gradient-to-br from-green-900/30 to-blue-900/30 rounded-2xl p-8 border-2 border-green-500 relative">
              {/* Popular Badge */}
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <div className="bg-green-500 text-black text-xs font-bold px-4 py-1 rounded-full">
                  MOST POPULAR
                </div>
              </div>

              <div className="text-center mb-8">
                <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">Pro Unlimited</h3>
                <div className="text-4xl font-bold mb-2">$25<span className="text-lg text-gray-400">/month</span></div>
                <p className="text-gray-400">Complete security solution</p>
              </div>

              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Unlimited security scans</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>All vulnerability detection</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Up to 100MB file uploads</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Privacy & compliance checks</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span><strong>25 AI fix recommendations/month</strong></span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>AI-powered code fix prompts</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-green-500" />
                  <span>Priority support</span>
                </li>
              </ul>

              <button
                onClick={() => handlePlanSelection('pro')}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Shield className="h-5 w-5" />
                    Start Pro Subscription
                  </>
                )}
              </button>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold mb-8">Frequently Asked Questions</h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto text-left">
              <div>
                <h3 className="font-semibold mb-2">What happens after I use my free scans?</h3>
                <p className="text-gray-400">You can purchase a Starter pack for $5 to get 10 more scans, or upgrade to Pro for unlimited scanning.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">What are AI fix recommendations?</h3>
                <p className="text-gray-400">Pro users get AI-powered prompts that explain vulnerabilities and provide step-by-step code fixes tailored to your development environment.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Can I cancel Pro anytime?</h3>
                <p className="text-gray-400">Yes, you can cancel your Pro subscription at any time. You'll continue to have access until the end of your billing period.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">What file types are supported?</h3>
                <p className="text-gray-400">We support ZIP archives containing your source code. Popular frameworks like React, Node.js, Python, PHP, and more are fully supported.</p>
              </div>
            </div>
          </div>

          {/* Security & Trust */}
          <div className="mt-16 text-center">
            <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Secure payment via Stripe</span>
              </div>
            </div>
          </div>

          {/* Authentication CTA */}
          {!user && (
            <div className="mt-12 text-center bg-gray-900 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-2">New to SafeVibe?</h3>
              <p className="text-gray-400 mb-4">Create a free account to get started with your security scans.</p>
              <div className="space-x-4">
                <button
                  onClick={() => navigate('/signup')}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition"
                >
                  Sign Up Free
                </button>
                <button
                  onClick={() => navigate('/login')}
                  className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-6 rounded-lg transition"
                >
                  Log In
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
} 